//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type AttachedDoc, type Class, type Doc, type Ref, type Timestamp } from '@hcengineering/core'
import { type Card } from '@hcengineering/card'
import { type Employee } from '@hcengineering/contact'
import { type NotificationGroup, type NotificationType } from '@hcengineering/notification'
import { type Milestone } from '@hcengineering/tracker'
import { type Asset, type IntlString, plugin, type Plugin } from '@hcengineering/platform'

/** @public */
export type GoalStatus = 'on_track' | 'at_risk' | 'off_track' | 'done'

/** @public */
export type KeyResultKind = 'metric' | 'milestone'

/**
 * Goal — strategic OKR (Card subtype, per spec §6.6).
 * @public
 */
export interface Goal extends Card {
  owner: Ref<Employee>
  cycle?: Ref<Cycle>
  dueDate?: Timestamp
  status: GoalStatus
  keyResults?: number
}

/**
 * KeyResult — child of Goal. Metric (manual check-in) OR Milestone-linked (auto progress).
 * @public
 */
export interface KeyResult extends AttachedDoc<Goal> {
  title: string
  kind: KeyResultKind
  owner: Ref<Employee>
  status: GoalStatus
  // metric-kind fields
  target?: number
  current?: number
  unit?: string
  // milestone-kind field
  milestone?: Ref<Milestone>
  checkIns?: number
}

/**
 * Cycle — e.g. "Q2 2026".
 * @public
 */
export interface Cycle extends Doc {
  name: string
  startDate: Timestamp
  endDate: Timestamp
}

/**
 * CheckIn — manual progress update on a KeyResult.
 * @public
 */
export interface CheckIn extends AttachedDoc<KeyResult> {
  value: number
  note?: string
  by: Ref<Employee>
  at: Timestamp
}

/** @public */
export const goalsId = 'goals' as Plugin

export default plugin(goalsId, {
  app: {
    Goals: '' as Ref<Doc>
  },
  class: {
    Goal: '' as Ref<Class<Goal>>,
    KeyResult: '' as Ref<Class<KeyResult>>,
    Cycle: '' as Ref<Class<Cycle>>,
    CheckIn: '' as Ref<Class<CheckIn>>
  },
  ids: {
    GoalsNotificationGroup: '' as Ref<NotificationGroup>,
    GoalAssignedNotification: '' as Ref<NotificationType>,
    KRAtRiskNotification: '' as Ref<NotificationType>,
    GoalDueSoonNotification: '' as Ref<NotificationType>,
    CheckInRequestedNotification: '' as Ref<NotificationType>
  },
  icon: {
    GoalsApplication: '' as Asset,
    Goal: '' as Asset,
    KeyResult: '' as Asset,
    Cycle: '' as Asset,
    CheckIn: '' as Asset
  },
  string: {
    ConfigLabel: '' as IntlString,
    ConfigDescription: '' as IntlString,
    Goal: '' as IntlString,
    Goals: '' as IntlString,
    KeyResult: '' as IntlString,
    KeyResults: '' as IntlString,
    Cycle: '' as IntlString,
    Cycles: '' as IntlString,
    CheckIn: '' as IntlString,
    CheckIns: '' as IntlString,
    Owner: '' as IntlString,
    DueDate: '' as IntlString,
    Status: '' as IntlString,
    Title: '' as IntlString,
    Kind: '' as IntlString,
    Target: '' as IntlString,
    Current: '' as IntlString,
    Unit: '' as IntlString,
    Milestone: '' as IntlString,
    StartDate: '' as IntlString,
    EndDate: '' as IntlString,
    Name: '' as IntlString,
    Value: '' as IntlString,
    Note: '' as IntlString,
    By: '' as IntlString,
    At: '' as IntlString,
    GoalApplication: '' as IntlString,
    GoalAssigned: '' as IntlString,
    KRAtRisk: '' as IntlString,
    GoalDueSoon: '' as IntlString,
    CheckInRequested: '' as IntlString,
    GoalsApplication: '' as IntlString,
    AllGoals: '' as IntlString,
    MyGoals: '' as IntlString
  }
})
